"use client";

import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

interface TopWeeklyItem {
  week: number;
  product_name: string;
  quantity: number;
}

interface Props {
  chartData: TopWeeklyItem[];
}

const TopSellingWeeklyChart: React.FC<Props> = ({ chartData }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);

    // Theme
    root.setThemes([am5themes_Animated.new(root)]);

    // Chart container
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
      })
    );

    // Prepare formatted data
    const formattedData = chartData.map(item => ({
      label: `Week ${item.week}`,
      product: item.product_name,
      value: item.quantity
    }));

    // X Axis (categories)
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "label",
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 10,
        })
      })
    );

    xAxis.data.setAll(formattedData);

    // Y Axis
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {})
      })
    );

    // Column series
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Quantity",
        xAxis,
        yAxis,
        valueYField: "value",
        categoryXField: "label",
        tooltip: am5.Tooltip.new(root, {
          labelText: "{categoryX}: {valueY}\n{product}"
        })
      })
    );

    // Style bars
    series.columns.template.setAll({
      cornerRadiusTL: 5,
      cornerRadiusTR: 5,
      strokeOpacity: 0,
    });

    series.data.setAll(formattedData);

    // Add cursor
    chart.set("cursor", am5xy.XYCursor.new(root, {}));

    // Cleanup on unmount
    return () => {
      root.dispose();
    };
  }, [chartData]);

  return (
    <div style={{ width: "100%", height: "100%" }} ref={chartRef}></div>
  );
};

export default TopSellingWeeklyChart;
